import type { ApiClient } from './client';

export interface Conversation {
  id: string;
  siteId: string;
  visitorId: string;
  /** Контакты visitor'а (ANX-029). Может отсутствовать в legacy-ответах
   *  или при visitor'е, удалённом из tenant'а. */
  visitor?: VisitorContacts;
  assignedOperatorId: string | null;
  status: 'open' | 'closed';
  lastMessageAt: string;
  closedAt: string | null;
  createdAt: string;
  /** ANX-043 — теги диалога. Если пусто — массив отсутствует (omitempty). */
  tags?: ConversationTagRef[];
  /** ANX-062 — CSAT-оценка визитёра: 1 (👍) / -1 (👎). undefined → не оценено. */
  csatScore?: 1 | -1;
  csatComment?: string;
  csatAt?: string;
  /** ANX-072 — выбранный визитёром в pre-chat форме отдел. Имя резолвится
   *  из списка departments на стороне панели. undefined → не выбран. */
  departmentId?: string;
  /** ANX-073 — с какого момента диалог ждёт ответа оператора (SLA-бейдж). */
  awaitingSince?: string;
  /** ANX-075 — момент последнего переоткрытия (бейдж «переоткрыт»). */
  reopenedAt?: string;
  /** ANX-065 — канал диалога: 'web' (виджет) | 'telegram'. */
  channel: 'web' | 'telegram';
}

export interface ConversationTagRef {
  id: string;
  name: string;
  color: string;
}

/**
 * ANX-120 — слияние conv-обновления с сохранением visitor-контактов.
 *
 * Мутационные ответы (close/assign/transfer) теперь несут visitor (бэкенд-фикс
 * ANX-120), но на случай legacy/частичного ответа без него не затираем уже
 * известные в кэше контакты — иначе шапка падает в «Гость … Контакты не
 * оставлены», хотя контакты были оставлены. prev может быть undefined.
 */
export function mergeConversation(
  prev: Conversation | undefined,
  updated: Conversation,
): Conversation {
  if (updated.visitor || !prev?.visitor) return updated;
  return { ...updated, visitor: prev.visitor };
}

export interface VisitorContacts {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
}

export interface Attachment {
  key: string;
  name: string;
  size: number;
  mimeType: string;
  url?: string;
}

export interface AttachmentInput {
  key: string;
  name: string;
  size: number;
  mimeType: string;
}

export interface Message {
  id: string;
  conversationId: string;
  seq: number;
  senderType: 'visitor' | 'operator' | 'system';
  senderId?: string;
  clientId?: string;
  type: string;
  body: string;
  createdAt: string;
  attachments?: Attachment[];
  /** ANX-119 — id цитируемого сообщения (reply). undefined → обычное. */
  replyToId?: string;
}

interface ConvRaw {
  id: string;
  site_id: string;
  visitor_id: string;
  visitor?: VisitorRaw | null;
  assigned_operator_id: string | null;
  status: string;
  last_message_at: string;
  closed_at: string | null;
  created_at: string;
  tags?: Array<{ id: string; name: string; color: string }>;
  csat_score?: number | null;
  csat_comment?: string | null;
  csat_at?: string | null;
  department_id?: string | null;
  awaiting_since?: string | null;
  reopened_at?: string | null;
  channel?: string | null;
}

interface VisitorRaw {
  id: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}

interface AttachmentRaw {
  key: string;
  name: string;
  size: number;
  mime_type: string;
  url?: string;
}

interface MessageRaw {
  id: string;
  conversation_id: string;
  seq: number;
  sender_type: string;
  sender_id?: string | null;
  client_id?: string | null;
  type: string;
  body: string;
  created_at: string;
  attachments?: AttachmentRaw[];
  reply_to_id?: string | null;
}

function toConv(r: ConvRaw): Conversation {
  const conv: Conversation = {
    id: r.id,
    siteId: r.site_id,
    visitorId: r.visitor_id,
    assignedOperatorId: r.assigned_operator_id,
    status: r.status as Conversation['status'],
    lastMessageAt: r.last_message_at,
    closedAt: r.closed_at,
    createdAt: r.created_at,
    channel: r.channel === 'telegram' ? 'telegram' : 'web',
  };
  if (r.visitor) {
    const v: VisitorContacts = { id: r.visitor.id };
    if (r.visitor.name) v.name = r.visitor.name;
    if (r.visitor.email) v.email = r.visitor.email;
    if (r.visitor.phone) v.phone = r.visitor.phone;
    conv.visitor = v;
  }
  if (r.tags && r.tags.length > 0) conv.tags = r.tags;
  if (r.csat_score === 1 || r.csat_score === -1) conv.csatScore = r.csat_score;
  if (r.csat_comment) conv.csatComment = r.csat_comment;
  if (r.csat_at) conv.csatAt = r.csat_at;
  if (r.department_id) conv.departmentId = r.department_id;
  if (r.awaiting_since) conv.awaitingSince = r.awaiting_since;
  if (r.reopened_at) conv.reopenedAt = r.reopened_at;
  return conv;
}

/**
 * ANX-099: payload WS-фрейма message.new — snake_case (как REST-ответы), но
 * приходит в обход HTTP-маппинга. Раньше ConversationView кастил его в Message
 * как есть: senderType/createdAt оставались пустыми (косметика, которую за ≤5с
 * лечил polling), а attachments[].mimeType === undefined ронял AttachmentView
 * TypeError'ом — весь ConversationView падал в error boundary при любой
 * картинке, пришедшей по WS (и приём от визитёра, и эхо своей отправки).
 */
export function wsMessageNewToMessage(payload: unknown): Message | null {
  const p = payload as Partial<MessageRaw> | undefined;
  if (
    !p ||
    typeof p.id !== 'string' ||
    typeof p.conversation_id !== 'string' ||
    typeof p.seq !== 'number' ||
    typeof p.body !== 'string' ||
    typeof p.sender_type !== 'string' ||
    typeof p.created_at !== 'string'
  ) {
    return null;
  }
  return toMessage({
    ...(p as MessageRaw),
    type: typeof p.type === 'string' ? p.type : 'text',
  });
}

function toMessage(r: MessageRaw): Message {
  const m: Message = {
    id: r.id,
    conversationId: r.conversation_id,
    seq: r.seq,
    senderType: r.sender_type as Message['senderType'],
    type: r.type,
    body: r.body,
    createdAt: r.created_at,
  };
  if (r.sender_id) m.senderId = r.sender_id;
  if (r.client_id) m.clientId = r.client_id;
  if (r.reply_to_id) m.replyToId = r.reply_to_id;
  if (r.attachments && r.attachments.length > 0) {
    m.attachments = r.attachments.map((a) => ({
      key: a.key,
      name: a.name,
      size: a.size,
      mimeType: a.mime_type,
      ...(a.url ? { url: a.url } : {}),
    }));
  }
  return m;
}

export interface ListConversationsParams {
  status?: 'open' | 'closed';
  /** 'me' — назначенные на текущего; 'unassigned' — без оператора; uuid — конкретный. */
  assignedTo?: 'me' | 'unassigned' | string;
  /** ANX-042: поисковая строка по visitor.name/email/phone + message.body. */
  q?: string;
  /** ANX-043: фильтр по конкретному tag'у (uuid). */
  tagId?: string;
  /** ANX-062: фильтр по CSAT-оценке. */
  csat?: 'positive' | 'negative' | 'any';
  limit?: number;
  cursor?: string;
}

export interface ListConversationsResult {
  conversations: Conversation[];
  nextCursor: string | null;
}

export async function listConversations(
  client: ApiClient,
  params: ListConversationsParams,
): Promise<ListConversationsResult> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.assignedTo) search.set('assigned_operator_id', params.assignedTo);
  if (params.q) search.set('q', params.q);
  if (params.tagId) search.set('tag_id', params.tagId);
  if (params.csat) search.set('csat', params.csat);
  if (params.limit) search.set('limit', String(params.limit));
  if (params.cursor) search.set('cursor', params.cursor);

  const qs = search.toString();
  const path = qs ? `/api/v1/conversations?${qs}` : '/api/v1/conversations';
  const raw = await client.request<{ conversations: ConvRaw[]; next_cursor: string }>(path);
  return {
    conversations: raw.conversations.map(toConv),
    nextCursor: raw.next_cursor === '' ? null : raw.next_cursor,
  };
}

export async function getConversation(
  client: ApiClient,
  id: string,
): Promise<Conversation> {
  const raw = await client.request<ConvRaw>(`/api/v1/conversations/${id}`);
  return toConv(raw);
}

export interface ListMessagesParams {
  sinceSeq?: number;
  limit?: number;
}

export async function listMessages(
  client: ApiClient,
  conversationId: string,
  params: ListMessagesParams,
): Promise<Message[]> {
  const search = new URLSearchParams();
  if (params.sinceSeq && params.sinceSeq > 0) {
    search.set('since_seq', String(params.sinceSeq));
  }
  if (params.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const path = qs
    ? `/api/v1/conversations/${conversationId}/messages?${qs}`
    : `/api/v1/conversations/${conversationId}/messages`;
  const raw = await client.request<{ messages: MessageRaw[] }>(path);
  return raw.messages.map(toMessage);
}

// ANX-049 — reverse infinite-scroll загрузка истории диалога.
export interface LoadHistoryParams {
  /** seq самого старого уже загруженного сообщения; 0/undefined = свежая страница. */
  beforeSeq?: number;
  limit?: number;
}

export interface LoadHistoryResult {
  messages: Message[];
  /** Есть ли ещё более старые сообщения в окне истории (90 дней). */
  hasMore: boolean;
}

export async function loadHistory(
  client: ApiClient,
  conversationId: string,
  params: LoadHistoryParams,
): Promise<LoadHistoryResult> {
  const search = new URLSearchParams();
  if (params.beforeSeq && params.beforeSeq > 0) {
    search.set('before_seq', String(params.beforeSeq));
  }
  if (params.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const path = qs
    ? `/api/v1/conversations/${conversationId}/messages?${qs}`
    : `/api/v1/conversations/${conversationId}/messages`;
  const raw = await client.request<{ messages: MessageRaw[]; has_more?: boolean }>(path);
  return { messages: raw.messages.map(toMessage), hasMore: raw.has_more === true };
}

// ANX-077 — поиск по сообщениям ВНУТРИ диалога. Возвращает совпавшие
// сообщения (с seq) для подсветки и навигации next/prev в ленте.
export async function searchMessages(
  client: ApiClient,
  conversationId: string,
  query: string,
): Promise<Message[]> {
  const q = query.trim();
  if (q === '') return [];
  const path = `/api/v1/conversations/${conversationId}/messages/search?q=${encodeURIComponent(q)}`;
  const raw = await client.request<{ messages: MessageRaw[] }>(path);
  return raw.messages.map(toMessage);
}

export interface SendMessageInput {
  clientId: string;
  body: string;
  attachments?: AttachmentInput[];
  /** ANX-119 — id сообщения, на которое отвечаем (цитата). */
  replyToId?: string;
}

export async function sendMessage(
  client: ApiClient,
  conversationId: string,
  input: SendMessageInput,
): Promise<Message> {
  const json: Record<string, unknown> = {
    client_id: input.clientId,
    body: input.body,
  };
  if (input.replyToId) json.reply_to_id = input.replyToId;
  if (input.attachments && input.attachments.length > 0) {
    json.attachments = input.attachments.map((a) => ({
      key: a.key,
      name: a.name,
      size: a.size,
      mime_type: a.mimeType,
    }));
  }
  const raw = await client.request<MessageRaw>(
    `/api/v1/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      json,
    },
  );
  return toMessage(raw);
}

export interface PatchConversationInput {
  assignedOperatorId?: string;
  unassignOperator?: boolean;
  status?: 'closed';
}

// ANX-050: operator self-copy транскрипта.
export async function sendTranscriptToOperator(
  client: ApiClient,
  conversationId: string,
): Promise<void> {
  await client.request<void>(
    `/api/v1/conversations/${conversationId}/transcript/me`,
    { method: 'POST' },
  );
}

export async function patchConversation(
  client: ApiClient,
  id: string,
  input: PatchConversationInput,
): Promise<Conversation> {
  const json: Record<string, unknown> = {};
  if (input.assignedOperatorId !== undefined) {
    json.assigned_operator_id = input.assignedOperatorId;
  }
  if (input.unassignOperator) {
    json.unassign_operator = true;
  }
  if (input.status !== undefined) {
    json.status = input.status;
  }
  const raw = await client.request<ConvRaw>(`/api/v1/conversations/${id}`, {
    method: 'PATCH',
    json,
  });
  return toConv(raw);
}

// ANX-074 — передача диалога коллеге с заметкой-причиной.
export async function transferConversation(
  client: ApiClient,
  id: string,
  operatorId: string,
  note?: string,
): Promise<Conversation> {
  const json: Record<string, unknown> = { operator_id: operatorId };
  if (note !== undefined && note.trim() !== '') json.note = note.trim();
  const raw = await client.request<ConvRaw>(`/api/v1/conversations/${id}/transfer`, {
    method: 'POST',
    json,
  });
  return toConv(raw);
}
